import React from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { SignIn, SignOut, User, Question } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import PhaseIndicator from "@/components/PhaseIndicator";

// Sticky header, two visual tiers:
//   • Top row (always visible, kept intentionally clean): logo on the left,
//     language toggle + FAQ + Accedi on the right. No stepper inside.
//   • Second row (also sticky, lives inside the same <header> element so
//     both rows pin together): the phase stepper. The mobile-abbreviated
//     variant renders on <lg; the desktop variant (32px circles, roomier
//     spacing, 13px full labels, fixed-height sub-label slot) renders on lg+.
export default function Header() {
  const { lang, setLang, t, user, logout } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  // Routes that render a phase stepper (keep in sync with PATH_TO_PHASE
  // inside PhaseIndicator). On other routes we skip the second row entirely
  // so pages like /, /quiz, /faq, /login stay single-row.
  const PHASE_ROUTES = new Set(["/organizza", "/checkin", "/abordo", "/dashboard"]);
  const showStepper = PHASE_ROUTES.has(location.pathname);

  return (
    <header className="sticky top-0 z-30 bg-surface/95 backdrop-blur-md border-b border-border">
      {/* Top row */}
      <div className="max-w-5xl mx-auto px-4 sm:px-5 py-3 flex items-center justify-between gap-2 sm:gap-3">
        <Link to="/" data-testid="header-logo" className="group shrink-0">
          <span className="font-display font-extrabold text-base sm:text-xl text-ink tracking-tight group-hover:text-terracotta transition-colors">
            {t.app.name}
          </span>
        </Link>

        <div className="flex items-center gap-1 sm:gap-2 shrink-0">
          <div
            role="tablist"
            aria-label={t.common.language}
            className="flex items-center gap-1 bg-surface-alt rounded-full p-0.5 sm:p-1 border border-border"
          >
            {["it", "en"].map((l) => (
              <button
                key={l}
                data-testid={`lang-${l}`}
                onClick={() => setLang(l)}
                className={`text-[10px] sm:text-xs font-semibold uppercase px-2 sm:px-3 py-0.5 sm:py-1 rounded-full transition-colors ${
                  lang === l ? "bg-terracotta text-white" : "text-ink-soft hover:text-ink"
                }`}
              >
                {l}
              </button>
            ))}
          </div>

          <Button
            data-testid="header-faq-btn"
            variant="ghost"
            size="sm"
            className="rounded-full font-sans px-2 sm:px-3"
            onClick={() => navigate("/faq")}
          >
            <Question size={18} weight="duotone" className="sm:mr-1" />
            <span className="hidden sm:inline">{t.nav.faq}</span>
          </Button>

          {user ? (
            <div className="flex items-center gap-1 sm:gap-2">
              <Button
                data-testid="header-dashboard-btn"
                variant="ghost"
                size="sm"
                className="rounded-full font-sans px-2 sm:px-3"
                onClick={() => navigate("/dashboard")}
              >
                <User size={18} weight="duotone" className="sm:mr-1" />
                <span className="hidden sm:inline">{t.nav.dashboard}</span>
              </Button>
              <Button
                data-testid="header-logout-btn"
                variant="ghost"
                size="sm"
                className="rounded-full font-sans text-ink-soft px-2"
                onClick={async () => { await logout(); navigate("/"); }}
              >
                <SignOut size={18} weight="duotone" />
              </Button>
            </div>
          ) : (
            <Button
              data-testid="header-login-btn"
              size="sm"
              className="rounded-full bg-terracotta hover:bg-terracotta-hover text-white font-sans font-semibold px-3 sm:px-4"
              onClick={() => navigate("/login")}
            >
              <SignIn size={14} weight="duotone" className="mr-1 shrink-0" />
              <span className="whitespace-nowrap">{t.nav.login}</span>
            </Button>
          )}
        </div>
      </div>

      {/* Second row — phase stepper. Rendered only on phase routes so
          non-phase pages (/, /quiz, /faq, /login, etc.) keep the previous
          single-row header appearance. */}
      {showStepper && (
        <div className="border-t border-border/60 bg-surface/95">
          <div className="hidden lg:block">
            <PhaseIndicator variant="desktop" />
          </div>
          <div className="lg:hidden">
            <PhaseIndicator variant="mobile" />
          </div>
        </div>
      )}
    </header>
  );
}
